import { useEffect, useState } from "react"
import { useAuth } from "@clerk/clerk-react"
import { motion } from "framer-motion"
import { Link } from "wouter"
import { Video, Settings, Loader2 } from "lucide-react"
import { useStudioStore } from "@/store/studio"
import { listProjects, usd } from "@/lib/api"
import type { Project } from "@/types"
import FilmGrain from "@/components/FilmGrain"
import { SignInButton } from "@clerk/clerk-react"

export default function DashboardPage() {
  const { getToken, isSignedIn } = useAuth()
  const store = useStudioStore()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isSignedIn) {
      setLoading(false)
      return
    }
    getToken().then(async (token) => {
      if (!token) return
      ;(window as unknown as Record<string, unknown>).__atlas_token__ = token
      try {
        const projs = await listProjects(token)
        setProjects(projs.projects || [])
      } catch {
        // ignore
      } finally {
        setLoading(false)
      }
    })
  }, [isSignedIn, getToken])

  return (
    <div className="min-h-screen bg-[#08090d] text-[#f0f1f5]">
      <FilmGrain />
      <header className="h-14 border-b border-[#1e2130] bg-[#08090d]/80 backdrop-blur-md flex items-center px-6 sticky top-0 z-40">
        <Link href="/">
          <a className="flex items-center gap-2">
            <Video className="w-5 h-5 text-[#f59e0b]" />
            <span className="font-semibold tracking-tight">Atlas Studio</span>
          </a>
        </Link>
      </header>

      <main className="max-w-5xl mx-auto p-3 sm:p-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="heading-display text-3xl mb-8">Dashboard</h1>

          {/* Credit & Plan */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <div className="surface p-4 sm:p-6">
              <p className="eyebrow mb-3">Available credit</p>
              <p className="text-3xl font-semibold mb-1">{store.identity ? usd(store.identity.creditMicros) : "—"}</p>
              <p className="text-sm text-[#9ca3af]">
                {store.identity
                  ? "Credit is spent only on renders you approve. Whatever a render does not use is returned."
                  : "Sign in to see your credit balance."}
              </p>
            </div>
            <div className="surface p-4 sm:p-6">
              <p className="eyebrow mb-3">Current plan</p>
              <p className="text-3xl font-semibold mb-1 capitalize">{store.billing?.plan || "Free"}</p>
              <p className="text-sm text-[#9ca3af]">
                {store.billing?.subscription ? "Manage your subscription in the billing portal." : "You are on the free plan."}
              </p>
            </div>
          </div>

          {/* Settings */}
          <div className="surface p-4 sm:p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#f59e0b]" />
              <h2 className="heading-sans text-lg">Studio settings</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Rendering preference</label>
                <select className="input" defaultValue="auto">
                  <option value="auto">Recommended · use my computer first</option>
                  <option value="cloud">Always use online rendering</option>
                  <option value="local">Only use my computer</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Maximum spend per project</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#9ca3af]">$</span>
                  <input
                    type="number"
                    min={1}
                    defaultValue={store.budget}
                    className="input"
                    onChange={(e) => store.setBudget(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Project Library */}
          <div className="surface p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div><h2 className="heading-sans text-lg">Movie & project library</h2><p className="text-xs text-[#6b7280] mt-0.5">Finished movies open directly in the player.</p></div>
              <Link href="/studio">
                <a className="btn-subtle text-xs">New project</a>
              </Link>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-[#9ca3af]" />
              </div>
            ) : !isSignedIn ? (
              <div className="py-8 text-center"><p className="text-sm text-[#9ca3af] mb-4">Sign in to see your projects and account balance.</p><SignInButton mode="modal" fallbackRedirectUrl="/dashboard"><button className="btn-primary text-sm">Sign in securely</button></SignInButton></div>
            ) : projects.length === 0 ? (
              <p className="text-sm text-[#9ca3af] py-8 text-center">This is your first project. Start creating above.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {projects.map((project) => (
                  <Link key={project.id} href={project.master_count ? "/studio/finish" : "/studio"}>
                    <a
                      onClick={() => {
                        store.resetProject()
                        store.setProject(project.id, project.title, project.concept || "")
                      }}
                      className="block p-4 rounded-lg border border-[#1e2130] bg-[#0f1117] hover:border-[#2d3148] transition-colors"
                    >
                      <p className="font-medium text-sm text-[#f0f1f5] truncate">{project.title || "Untitled project"}</p>
                      <p className="text-xs text-[#6b7280] mt-1">
                        {project.master_count
                          ? `${project.master_count} finished movie${project.master_count > 1 ? "s" : ""} · Open player`
                          : project.render_count
                            ? `${project.render_count} render${project.render_count > 1 ? "s" : ""}`
                            : "In development"}
                      </p>
                    </a>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  )
}
